/**
 * 페이지에 붙는 전송 런타임 (TASK-2026-004 SPEC 「포함 범위」 4·5).
 *
 * 게임(`/play/`)과 설문(`/collect.html`)은 서로 다른 페이지지만 같은 origin·같은 저장소를
 * 쓴다. 두 페이지 모두 자기 화면이 살아 있는 동안 큐를 밀어야 한다 — 게임만 보내면 설문
 * 화면에서 남은 자료가 멈추고, 설문만 보내면 플레이 도중 아무것도 올라가지 않는다.
 *
 * **온라인 세션이 없으면 아무것도 만들지 않는다.** 연습 모드와 동의 전에는 전송기 자체가
 * 없으므로 원격 요청이 0이다(OF1-02).
 */

import { checkpointsFor } from '../app/checkpoint';
import { confirmationSpoolFor, telemetryFor } from '../app/telemetry';
import { PROTOTYPE_STAGES } from '../stages/fixtures';
import { contentHash } from '../telemetry/canonical';
import type { Api } from './api';
import { PAGE_CLOCK } from './clock';
import { createConnectivity, probeHealth, probeReadyz, type Connectivity } from './connectivity';
import { onFlushRequested } from './flushSignal';
import { createLease, LEASE_RENEW_MS } from './lease';
import { createSender, type FlushOutcome, type Sender, type SenderStatus } from './sender';
import { readSessionState, type OnlineSessionState } from './state';

/** 등록된 stage의 content hash. 서버가 같은 값을 계산한다. */
export const STAGE_HASHES: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(PROTOTYPE_STAGES.map((stage) => [stage.stage_id, contentHash(stage)])),
);

let connectivity: Connectivity | undefined;

/**
 * 이 페이지의 서버 연결 점검 (TASK-2026-012 CR-01).
 *
 * 페이지마다 하나다. 수집 화면의 버튼·표시와 전송기가 같은 판정을 본다 —— 화면만
 * 온라인으로 바꾸고 전송기는 `navigator.onLine`에 막혀 있는 상태를 만들지 않는다.
 * 연습 모드처럼 온라인 세션이 없는 화면은 이 함수를 부르지 않으므로 점검 요청도 없다.
 */
export function pageConnectivity(): Connectivity {
  connectivity ??= createConnectivity({
    probe: probeReadyz,
    // 연결된 채 대기할 때는 Firestore를 읽지 않는 /health로만 도달성을 본다 (CR-02).
    idleProbe: probeHealth,
    hintOnline: () => window.navigator.onLine !== false,
    visible: () => window.document.visibilityState !== 'hidden',
    now: () => Date.now(),
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: (handle) => {
      window.clearTimeout(handle as number);
    },
    listen: (event, handler) => {
      const target = event === 'visibilitychange' ? window.document : window;
      target.addEventListener(event, handler);
      return () => {
        target.removeEventListener(event, handler);
      };
    },
  });
  return connectivity;
}

/** 이 탭의 식별자. 저장하지 않고 이번 실행 동안만 쓴다. */
export function newTabId(): string {
  return `tab-${String(Date.now())}-${String(Math.floor(Math.random() * 1e9))}`;
}

/**
 * 이 페이지의 전송기를 만든다.
 *
 * 로그·checkpoint·확인 기록 버퍼를 **세션 이름으로** 집는다 (R2-REV-001). 지금 화면이
 * 어느 출처로 기록하는지에 기대지 않으므로, 같은 페이지에서 연습 기록이 남아 있어도
 * 전송 대상은 이 세션의 키 공간뿐이다.
 */
export function createPageSender(api: Api, session: OnlineSessionState): Sender {
  const storage = window.localStorage;
  const sessionRef = session.session_ref;
  return createSender({
    client: api.client,
    log: telemetryFor(sessionRef),
    checkpoint: () => checkpointsFor(sessionRef).read(),
    confirmations: confirmationSpoolFor(sessionRef),
    storage,
    lease: createLease({ storage, tabId: newTabId(), sessionRef, now: () => Date.now() }),
    sessionRef,
    /*
     * 세션 조회 결과가 **이 참여**일 때만 쓰기 토큰을 받는다 (R2-REV-003). 동의 버전은
     * 페이지 진입 판정(collect `restore`, 게임 `decidePlayGate`)이 이미 대조했다.
     */
    adoptSession: (server) => {
      if (server.campaign_id !== session.campaign_id || server.session_ref !== sessionRef) {
        return false;
      }
      api.setCsrf(server.csrf_token);
      return true;
    },
    stageHashes: STAGE_HASHES,
    now: () => Date.now(),
    /*
     * 실제 점검 결과가 브라우저 표시보다 앞선다 (CR-01 §4). 점검이 연결을 확인하면 표시가
     * 거짓이어도 보내고, 연결 안 됨을 확인하면 성공할 수 없는 자동 전송을 반복하지 않는다.
     * 점검 전에는 표시를 따른다. 어느 쪽이든 결과는 서버 ACK로만 판정한다.
     */
    online: () => pageConnectivity().online(),
    // 페이지 로드마다 한 줄. 이벤트 시각을 어느 페이지의 시계가 찍었는지 남긴다.
    segment: { id: PAGE_CLOCK.id, started_at: PAGE_CLOCK.startedAt },
  });
}

/** 저장된 세션 표식. 없으면 이 페이지는 아무것도 보내지 않는다. */
export function currentSession(): OnlineSessionState | undefined {
  try {
    return readSessionState(window.localStorage);
  } catch {
    return undefined;
  }
}

export interface LoopOptions {
  /** 상태가 바뀔 때마다 부른다. 화면이 저장 상태를 사실대로 보여 주는 데 쓴다. */
  readonly onStatus?: (status: SenderStatus) => void;
  /** 검사용. 기본은 1초다. */
  readonly tickMs?: number;
  /** 쓰기 임차 갱신 간격. 기본은 `LEASE_RENEW_MS`(3초)다. */
  readonly renewMs?: number;
  /** 서버 연결 점검. 기본은 이 페이지의 점검이다(CR-01). */
  readonly connectivity?: Connectivity;
  /**
   * 유휴 `/health` 점검을 할 활성 참가인가 (CR-02). 기본은 참이다. 전송 주기는 동의한 세션이
   * 있을 때만 돌므로 동의 전·연습 모드에는 걸리지 않는다. 제출을 마친 화면처럼 활성 참가가
   * 아닌 때는 거짓을 돌려준다.
   */
  readonly idleActive?: () => boolean;
  /**
   * 전송을 잠시 멈출 때인가 (TASK-2026-012 CR-03). 기본은 거짓이다.
   *
   * 되살아났거나 다시 보인 참가 화면이 이 참여가 아직 열려 있는지 서버에 다시 묻는 동안 참을
   * 돌려준다. 그동안은 어떤 경로로도 보내지 않는다. 숨을 때 임차를 놓는 일은 그대로 한다.
   */
  readonly suspended?: () => boolean;
}

/** 네트워크가 닿지 않아 실패한 결과인가. 서버가 답한 거절은 연결 문제가 아니다. */
function unreachable(outcome: FlushOutcome): boolean {
  return outcome.kind === 'retry' && outcome.code === 'network_error';
}

/**
 * 전송 주기를 건다. 돌려받은 함수를 부르면 멈추고 쓰기 임차를 놓아 준다.
 *
 * 주기는 1초마다 조건을 보고(10초 경과 또는 유효 이동 50회) 필요할 때만 보낸다. 화면을
 * 떠날 때는 마지막으로 한 번 보내 보지만 도착은 보장하지 않는다 —— 남은 자료는 다음
 * 실행이 큐에서 이어 보낸다(중앙 명세 F1-06).
 */
export function startSenderLoop(sender: Sender, options: LoopOptions = {}): () => void {
  /** 이 주기가 이미 내려갔는가. 정리는 한 번만 한다. */
  let halted = false;
  /** 타이머·listener를 모두 건 뒤인가. 그 전에 봉인을 보면 다 건 다음에 내린다. */
  let started = false;
  let haltRequested = false;
  const report = () => {
    const status = sender.status();
    options.onStatus?.(status);
    /*
     * 서버가 완료를 확인해 봉인한 세션은 더 보낼 것도, 쓸 것도 없다 (TASK-2026-012 CR-03
     * REV-CR03-003). 화면이 완료로 닫히는 것과 함께 주기 자체를 내린다 —— 남겨 두면 완료
     * 화면에서도 임차를 3초마다 갱신하고 유휴 `/health`를 60초마다 부른다. 상태 보고 뒤에
     * 내리므로 화면은 봉인 상태를 먼저 받는다.
     */
    if (status.sealed === true) {
      if (started) {
        halt();
      } else {
        haltRequested = true;
      }
    }
  };
  const hidden = (): boolean => window.document.visibilityState === 'hidden';
  const suspended = options.suspended ?? (() => false);
  const link = options.connectivity ?? pageConnectivity();
  /** 결과를 보고, 연결이 끊겨 실패했으면 연결을 다시 점검하게 한다(CR-01 §2). */
  const settle = (outcome: FlushOutcome) => {
    if (unreachable(outcome)) {
      link.reportFailure();
    }
    report();
  };

  const timer = window.setInterval(() => {
    /*
     * 보이지 않는 탭은 보내지 않는다. 쓰기 임차가 하나뿐이라(F1-05) 숨은 탭이 계속
     * 갱신하면 참가자가 보고 있는 탭이 영영 쓰지 못한다. 실제로 게임 탭을 남긴 채 설문
     * 탭을 열었을 때 설문의 마지막 전송이 막혔다.
     */
    if (hidden() || suspended()) {
      return;
    }
    void sender.tick().then(settle, report);
  }, options.tickMs ?? 1_000);

  /*
   * 쓰기 임차는 **전송과 따로** 갱신한다 (REV-002).
   *
   * 예전에는 flush가 due일 때만 `acquire()`가 불렸다. 전송 주기는 10초이고 임차
   * TTL은 8초라, 보내지 않는 2초 동안 임차가 만료돼 다른 탭이 가져갔다. 그러면 한
   * 세션에 두 탭이 번갈아 쓰고 같은 순번을 서로 보낸다. 갱신은 TTL의 절반보다 짧은
   * 간격으로 돌고, 화면이 이 결과를 보고 자기가 쓰는 탭인지 판단한다.
   */
  const renew = () => {
    if (halted || hidden()) {
      return;
    }
    sender.hold();
    report();
  };
  const leaseTimer = window.setInterval(renew, options.renewMs ?? LEASE_RENEW_MS);

  const flush = () => {
    if (halted || hidden() || suspended()) {
      return;
    }
    void sender.flushAll().then(settle, report);
  };

  /*
   * 점검이 연결을 확인하면 같은 세션을 다시 확인하고 이어 보낸다 (CR-01 §4). 세션 조회가
   * 쓰기 토큰과 참여 일치를 확인하고, 전송은 ACK로만 표식을 옮긴다.
   */
  const stopLink = link.start();
  // 연결된 채 대기하는 동안의 끊김을 감지한다 (CR-02). 보이는 활성 참가 화면에서만 돈다.
  const stopIdle = link.watchIdle(options.idleActive ?? (() => true));
  /*
   * **연결되지 않았던 상태에서** 연결됨으로 바뀔 때만 이어 보낸다. 이미 연결된 채로 실패 보고
   * 때문에 다시 점검한 경우까지 보내면, batch만 계속 실패할 때 backoff를 건너뛰고 몇 초마다
   * 다시 보내게 된다.
   */
  let settled = link.state() === 'connected' ? 'connected' : 'other';
  const stopWatch = link.subscribe((state) => {
    if (state === 'checking') {
      return;
    }
    const before = settled;
    settled = state === 'connected' ? 'connected' : 'other';
    if (state !== 'connected' || before === 'connected' || hidden() || suspended()) {
      return;
    }
    void sender.resync().then(() => {
      flush();
    }, report);
  });

  const visibility = () => {
    if (hidden()) {
      /*
       * 숨기 전에 **한 번 보내고** 놓는다 (REV-003·REV-002).
       *
       * 목표 오픈·시도 종료와 달리 여기서는 마지막 구간 전체가 대상이다. 보낸 뒤에
       * 놓아야 보이는 탭이 곧바로 이어받을 수 있고, 놓은 뒤에 다시 가져가는 경로가
       * 생기지 않는다. 도착을 보장하지는 않는다 —— 큐는 durable하므로 남은 자료는
       * 다음 실행이 이어 보낸다(F1-06).
       *
       * 다시 묻는 중이면 보내지 않고 놓기만 한다 (CR-03).
       */
      if (suspended()) {
        sender.release();
        report();
        return;
      }
      void sender.flushAll().then(
        () => {
          sender.release();
          report();
        },
        () => {
          sender.release();
          report();
        },
      );
      return;
    }
    /*
     * 다시 보이면 쓸 수 있는 상태로 되돌린다. `resume()` 없이 flush만 부르면 앞서
     * 놓은 전송기가 영원히 `not_holder`로 남는다.
     */
    sender.resume();
    renew();
    flush();
  };

  const leave = () => {
    /*
     * 페이지를 떠난다. **놓기만 한다** (REV-002).
     *
     * 예전에는 놓은 직후 `flushAll()`을 불렀는데, 그 호출이 같은 자리에서 임차를
     * 도로 가져가 "놓았다"가 사실이 아니게 됐다. 이 뒤의 비동기 작업은 끝까지 돌지
     * 못할 수 있으므로 전송을 여기에 걸지 않는다. 남은 자료는 큐에 durable하게
     * 남아 다음 화면·다음 탭이 이어 보낸다(F1-06).
     */
    sender.release();
  };

  /** 목표 오픈·시도 종료가 기록되면 그 자리에서 보낸다(REV-003). */
  const stopSignal = onFlushRequested(flush);

  window.addEventListener('online', flush);
  window.addEventListener('visibilitychange', visibility);
  window.addEventListener('pagehide', leave);

  /** 주기를 내린다. 여러 번 불러도 한 번만 정리한다(봉인과 화면의 effect 정리가 함께 부를 수 있다). */
  function halt(): void {
    if (halted) {
      return;
    }
    halted = true;
    window.clearInterval(timer);
    window.clearInterval(leaseTimer);
    stopSignal();
    stopWatch();
    stopIdle();
    stopLink();
    window.removeEventListener('online', flush);
    window.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('pagehide', leave);
    sender.release();
  }
  started = true;
  renew();
  flush();
  if (haltRequested) {
    halt();
  }

  return halt;
}
